import Axios from "../config/axios-v1-config";

const BASE = "/billing";

// Pre-documents
export const listPreDocuments = (params) =>
  Axios.get(`${BASE}/pre-documents`, { params });

export const getPreDocument = (id) =>
  Axios.get(`${BASE}/pre-documents/${id}`);

export const generateClientPreDocument = (data) =>
  Axios.post(`${BASE}/pre-documents/generate/client`, data);

export const generateDriverPreDocument = (data) =>
  Axios.post(`${BASE}/pre-documents/generate/driver`, data);

export const updateLineItem = (id, idx, data) =>
  Axios.patch(`${BASE}/pre-documents/${id}/line-items/${idx}`, data);

export const addDeduction = (id, data) =>
  Axios.post(`${BASE}/pre-documents/${id}/deductions`, data);

export const removeDeduction = (id, idx) =>
  Axios.delete(`${BASE}/pre-documents/${id}/deductions/${idx}`);

export const approvePreDocument = (id) =>
  Axios.post(`${BASE}/pre-documents/${id}/approve`);

export const rejectPreDocument = (id, data) =>
  Axios.post(`${BASE}/pre-documents/${id}/reject`, data);

export const sendPreDocumentToBsale = (id) =>
  Axios.post(`${BASE}/pre-documents/${id}/send-to-bsale`);

// Driver tariffs
export const listDriverTariffs = (driverId) =>
  Axios.get(`${BASE}/driver-tariffs`, { params: driverId ? { driverId } : {} });

export const getDriverTariff = (id) =>
  Axios.get(`${BASE}/driver-tariffs/${id}`);

export const createDriverTariff = (data) =>
  Axios.post(`${BASE}/driver-tariffs`, data);

export const updateDriverTariff = (id, data) =>
  Axios.patch(`${BASE}/driver-tariffs/${id}`, data);

// Driver payment config (pago a repartidores por comuna + sobredimensionado).
// Va bajo /billing/driver-payment-config, que el BFF protege con AdminOnly.
export const getDriverPaymentConfig = () =>
  Axios.get(`${BASE}/driver-payment-config`);

export const getDriverPaymentConfigVersions = () =>
  Axios.get(`${BASE}/driver-payment-config/versions`);

export const saveDriverPaymentConfig = (data) =>
  Axios.put(`${BASE}/driver-payment-config`, data);

export const getDriverPaymentCoverageCheck = (days = 30) =>
  Axios.get(`${BASE}/driver-payment-config/coverage-check`, { params: { days } });

// Liquidación a repartidores en un rango (vista del operador). Mismo cálculo que ve el
// repartidor en la app. `tz` es la zona horaria del navegador para acotar los días.
export const getDriverPayrollSummary = ({ from, to, tz }) =>
  Axios.get(`${BASE}/driver-payroll/summary`, { params: { from, to, tz } });

// Detalle de un día de un repartidor (paquetes + estado), para gestionar retenciones.
export const getDriverPayrollDay = ({ driverId, date, tz }) =>
  Axios.get(`${BASE}/driver-payroll/day`, { params: { driverId, date, tz } });

// Acciones de pago por día (operador).
export const markDriverDayPaid = (data) =>
  Axios.post(`${BASE}/driver-payroll/day/mark-paid`, data);

export const markDriverDaysPaidBulk = (data) =>
  Axios.post(`${BASE}/driver-payroll/mark-paid-bulk`, data);

export const addDriverDayExtra = (data) =>
  Axios.post(`${BASE}/driver-payroll/day/extra`, data);

export const removeDriverDayExtra = (data) =>
  Axios.post(`${BASE}/driver-payroll/day/remove-extra`, data);

export const withholdDriverDayPackage = (data) =>
  Axios.post(`${BASE}/driver-payroll/day/withhold`, data);

export const releaseDriverDayPackage = (data) =>
  Axios.post(`${BASE}/driver-payroll/day/release`, data);

// Carga masiva de extras (Excel). `items` ya vienen resueltos a driverId.
export const addDriverExtrasBatch = (data) =>
  Axios.post(`${BASE}/driver-payroll/extras-batch`, data, { timeout: 300000 });

export const rollbackDriverExtrasBatch = (batchId) =>
  Axios.post(`${BASE}/driver-payroll/extras-batch/rollback`, { batchId });

export const listDriverExtrasBatches = () =>
  Axios.get(`${BASE}/driver-payroll/extras-batches`);

// Filas aplicadas de un lote (con nombre de repartidor): alimenta el Excel de auditoría.
export const getDriverExtrasBatchItems = (batchId) =>
  Axios.get(`${BASE}/driver-payroll/extras-batch/${batchId}/items`);

// Marcar pagado/no pagado una selección de (driverId, date).
export const markDriverDaysPaidSelection = (data) =>
  Axios.post(`${BASE}/driver-payroll/mark-paid-selection`, data);

// Fija la fecha de pago (YYYY-MM-DD) de días YA pagados, sin cambiar su estado.
export const setDriverPaymentDate = (data) =>
  Axios.post(`${BASE}/driver-payroll/payment-date`, data);

// Planilla de pago para el banco (formato pago masivo Santander): transferencias a
// repartidores del período. Devuelve `origin` (cuenta/moneda del operador), `rows`
// (repartidores con monto>0 y datos bancarios completos) y `missing` (con monto pero
// sin datos bancarios). `tz` acota los días igual que en el resto de la liquidación.
export const getDriverPaymentBankExport = ({ from, to, tz }) =>
  Axios.get(`${BASE}/driver-payroll/bank-export`, { params: { from, to, tz } });

// Notifica por correo a cada repartidor (no-freelancer, con monto pendiente > 0 en el
// período) cuánto debe facturar/boletear. Con `dryRun: true` NO envía nada y devuelve en
// `recipients` a quiénes se les enviaría; sin dryRun ejecuta el envío real. El envío real
// manda un correo por repartidor, así que le damos un timeout amplio (120s).
export const notifyDriverPayrollAmounts = ({ from, to, tz, dryRun }) =>
  Axios.post(
    `${BASE}/driver-payroll/notify-amounts`,
    { from, to, tz, dryRun },
    { timeout: 120000 },
  );

// Rechaza el documento tributario (factura/boleta en PDF) que el repartidor subió por una
// semana, con motivo. No borra el archivo: lo marca rechazado para que el repartidor vea el
// motivo en la app y suba otro. `weekStart` es el domingo de la semana (lo trae cada
// documento de `driver-payroll/summary`).
export const rejectDriverBillingDocument = ({ driverId, weekStart, reason }) =>
  Axios.post(`${BASE}/driver-payroll/document/reject`, {
    driverId,
    weekStart,
    reason,
  });

// Acepta (da por válido) el documento tributario de una semana. Es el visto bueno
// explícito del operador: el documento queda `accepted` y deja de aparecer como
// pendiente de validar. Se puede revertir rechazándolo (el backend limpia el estado).
// `meta` (opcional) son los datos IMPRESOS del documento — emisor, RUT, folio, fechas,
// monto —; si viene, el backend la registra ANTES con control de duplicados: un
// (RUT, folio) ya registrado en otra semana/cuenta responde 409 y NO acepta.
// `docType` (opcional) es el tipo que CONFIRMA el operador ('factura' | 'boleta'; por
// defecto el del documento). El backend exige el monto (400 code 'amount_required') y lo
// cruza contra el esperado para ese tipo: si no cuadra responde 409 code 'amount_mismatch'
// con `details: { docType, declared, expected: { factura, boleta } }`, salvo que venga
// `amountOverrideReason` (3..300 caracteres), que queda registrado con la aceptación.
export const acceptDriverBillingDocument = ({ driverId, weekStart, meta, docType, amountOverrideReason }) =>
  Axios.post(`${BASE}/driver-payroll/document/accept`, {
    driverId,
    weekStart,
    ...(meta ? { meta } : {}),
    ...(docType ? { docType } : {}),
    ...(amountOverrideReason ? { amountOverrideReason } : {}),
  });

// Registra (o corrige) los datos impresos del documento de una semana sin cambiar su
// estado. Mismo control de duplicados (409) que el accept-con-meta.
export const setDriverBillingDocumentMeta = ({ driverId, weekStart, ...meta }) =>
  Axios.post(`${BASE}/driver-payroll/document/meta`, {
    driverId,
    weekStart,
    ...meta,
  });

// Pago contra entrega (COD) — configuración del operador. Versionada e inmutable:
// el PUT crea una versión nueva y archiva la anterior (la liquidación al comercio
// necesita reconstruir con qué reglas se cobró cada paquete). La moneda NO se manda:
// el backend la resuelve del país de operación del operador.
export const getCodConfig = () =>
  Axios.get(`${BASE}/cod-config`);

export const getCodConfigVersions = () =>
  Axios.get(`${BASE}/cod-config/versions`);

export const saveCodConfig = (data) =>
  Axios.put(`${BASE}/cod-config`, data);

// Tarifa base de cobro a comercios (nivel operador). A diferencia de cod-config y
// driver-payment-config, NO es versionada: un documento por operador que se pisa al
// editar. Es el nivel BASE de la cascada de precios — el contrato de cada comercio
// sobreescribe lo que quiera y hereda el resto.
//
// El `configVersion` que devuelve el GET no es historial: viaja en la vista previa del
// cobro y vuelve al generar, para que editar la tarifa entre un paso y otro no emita
// notas con un total distinto al confirmado.
// Va bajo /billing/commerce-pricing-config, que el BFF protege con AdminOnly.
export const getCommercePricingConfig = () =>
  Axios.get(`${BASE}/commerce-pricing-config`);

export const saveCommercePricingConfig = (data) =>
  Axios.put(`${BASE}/commerce-pricing-config`, data);

// Escala de tarifa por volumen efectiva de cada comercio del operador
// ([{ companyId, scale, source }]). La resuelve el backend —contrato ?? default del
// operador, degradando a 'flat' si la escala no tiene tramos— para que el wizard de cobro
// pueda filtrar los comercios por ciclo con el MISMO criterio con el que después se emite.
//
// Cuelga de /commerce-pricing-config a propósito: ese prefijo ya está declarado en el BFF
// con AdminOnly. Un prefijo nuevo bajo /billing caería al catch-all, que no lo lleva.
export const getCommerceScales = () =>
  Axios.get(`${BASE}/commerce-pricing-config/commerce-scales`);

// Tarifa propia de un holding del operador (su nota consolidada). Misma forma que la
// tarifa de un contrato, versionada con "vigente desde". También cuelga de
// /commerce-pricing-config por el AdminOnly del BFF, y billing la vuelve a autorizar por rol.
// GET → { data: { holdingId, exists, pricing|null, pricingVersion (0 sin tarifa),
//   pricingHistory: [{ version, effectiveFrom, createdAt }], effectiveVersionToday|null,
//   updatedAt, updatedBy } }. 404 holding_not_found · 503 holdings_unavailable.
export const getHoldingPricing = (holdingId) =>
  Axios.get(`${BASE}/commerce-pricing-config/holdings/${encodeURIComponent(holdingId)}`);

// Body { pricing, pricingEffectiveFrom?: 'YYYY-MM-DD', expectedPricingVersion: number }:
// REEMPLAZA la tarifa entera y crea la versión N+1 (vigente desde el inicio de ese día del
// operador; sin fecha, desde ahora). `expectedPricingVersion` es la versión sobre la que se
// editó (0 si no había tarifa): si no es la guardada, 409 pricing_version_conflict (otra sesión
// guardó antes: recargar) y no se pisa nada. Responde la misma vista que el GET.
// 400 validación · 404 holding_not_found · 503 holdings_unavailable.
export const saveHoldingPricing = (holdingId, body) =>
  Axios.put(`${BASE}/commerce-pricing-config/holdings/${encodeURIComponent(holdingId)}`, body);

// Subida ADMIN del documento tributario (PDF) en nombre del repartidor: para cuando el
// documento existe pero el repartidor no puede subirlo (app, teléfono, licencia…).
// `date` es cualquier día de la semana del documento (el backend la normaliza a dom–sáb)
// y `docType` fuerza factura/boleta si difiere del de la ficha. No dispara correo.
export const uploadDriverBillingDocument = ({ driverId, date, docType, file, tz }) => {
  const form = new FormData();
  form.append('file', file);
  form.append('driverId', driverId);
  form.append('date', date);
  if (docType) form.append('docType', docType);
  if (tz) form.append('tz', tz);
  return Axios.post(`${BASE}/driver-payroll/document/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
};

// Validación con IA de los documentos tributarios pendientes (cola "Por validar").
// Todo cuelga de /driver-payroll/ai-validation. El estado dice si la función existe
// para este operador (`enabled`), si corre en sombra (`mode: 'shadow'` = simula, no
// acepta) y si hay un job en curso; un 404/501 acá se trata en el front como
// deshabilitado, sin avisar.
export const getAiValidationStatus = () =>
  Axios.get(`${BASE}/driver-payroll/ai-validation/status`);

// Dry-run: cuántos documentos se validarían y cuánto tardaría. No dispara nada.
export const previewAiValidation = ({ items }) =>
  Axios.post(`${BASE}/driver-payroll/ai-validation/preview`, { items });

// Arranca el job (202 {jobId,total}). 409 = ya hay uno activo (body {jobId}).
export const startAiValidation = ({ items }) =>
  Axios.post(`${BASE}/driver-payroll/ai-validation`, { items });

// Contadores en vivo del job; se consulta cada pocos segundos hasta un estado terminal.
export const getAiValidationProgress = (jobId) =>
  Axios.get(`${BASE}/driver-payroll/ai-validation/${jobId}/progress`);

// Detalle del job con la decisión y las lecturas de cada documento.
export const getAiValidation = (jobId) =>
  Axios.get(`${BASE}/driver-payroll/ai-validation/${jobId}`);

// Historial de jobs (sin items), del más reciente al más antiguo.
export const listAiValidations = ({ limit = 20 } = {}) =>
  Axios.get(`${BASE}/driver-payroll/ai-validations`, { params: { limit } });

// Deshace las aceptaciones de un job (vuelven a 'uploaded'). Devuelve
// {reverted, blocked, skipped}: `blocked` son las que ya se pagaron y no se tocan.
export const rollbackAiValidation = (jobId) =>
  Axios.post(`${BASE}/driver-payroll/ai-validation/${jobId}/rollback`);

// Avisos por correo de pago a repartidores (por operador). GET trae la configuración +
// `candidates` (usuarios activos del equipo elegibles como destinatarios).
export const getDriverBillingNotificationSettings = () =>
  Axios.get(`${BASE}/driver-payroll/notification-settings`);

// {documentUploadedEnabled: boolean, documentUploadedRecipients: string[]} — lista vacía =
// todos los administradores activos.
export const updateDriverBillingNotificationSettings = (data) =>
  Axios.put(`${BASE}/driver-payroll/notification-settings`, data);
